import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Badge,
} from '@mui/material';
import {
  Restaurant,
  ShoppingCart,
  CalendarMonth,
  AddCircleOutline,
  CheckCircle,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import utc from 'dayjs/plugin/utc';

dayjs.extend(isoWeek);
dayjs.extend(isSameOrAfter);
dayjs.extend(utc);

interface Meal {
  meal_id: number;
  meal_type: string;
  assigned_date: string;
  recipe: {
    recipe_id: number;
    name: string;
    image_url?: string;
  };
  status?: string;
  is_leftover?: boolean;
  extra_servings?: number;
  servings?: number;
}

interface MealPlan {
  plan_id: number;
  week_start_date: string;
  // Real first/last meal dates returned by /meal-plans/user-plans — preferred
  // over week_start_date + 6 because they're accurate for partial weeks.
  actual_start_date?: string;
  actual_end_date?: string;
  status: string;
  meals?: Meal[];
}

interface ShoppingListStatus {
  total_items: number;
  checked_items: number;
  plan_id: number;
}

const mealTypeLabels: Record<string, string> = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
};

const getMealTypeColor = (mealType: string) => {
  switch (mealType.toLowerCase()) {
    case 'breakfast': return 'primary';
    case 'lunch': return 'secondary';
    case 'dinner': return 'error';
    default: return 'default';
  }
};

/** Reusable image-based meal card for the dashboard */
const DashboardMealCard: React.FC<{
  meal: Meal;
  onClick: () => void;
  height?: number;
  showDate?: boolean;
}> = ({ meal, onClick, height = 140, showDate = false }) => {
  const isLeftover = meal.is_leftover;
  const isCompleted = meal.status === 'completed';

  return (
    <Badge
      badgeContent={
        isCompleted ? (
          <CheckCircle
            sx={{ color: 'success.main', fontSize: 22, bgcolor: 'white', borderRadius: '50%' }}
          />
        ) : null
      }
      sx={{ '& .MuiBadge-badge': { right: 10, top: 10 }, width: '100%' }}
    >
      <Card
        elevation={2}
        sx={{
          width: '100%',
          cursor: 'pointer',
          overflow: 'hidden',
          border: isCompleted ? '2px solid' : isLeftover ? '2px dashed' : '1px solid',
          borderColor: isCompleted ? 'success.main' : isLeftover ? 'info.light' : 'divider',
          borderRadius: 2,
          '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
          transition: 'box-shadow 0.2s, transform 0.2s',
        }}
        onClick={onClick}
      >
        <Box sx={{ position: 'relative', height, overflow: 'hidden' }}>
          {/* Image or placeholder */}
          {meal.recipe.image_url ? (
            <Box
              component="img"
              src={meal.recipe.image_url}
              alt={meal.recipe.name}
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              sx={{
                position: 'absolute', top: 0, left: 0,
                width: '100%', height: '100%', objectFit: 'cover',
              }}
            />
          ) : (
            <Box sx={{
              position: 'absolute', top: 0, left: 0,
              width: '100%', height: '100%',
              bgcolor: 'grey.200',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Restaurant sx={{ fontSize: 40, color: 'grey.400' }} />
            </Box>
          )}

          {/* Top badges */}
          <Box sx={{
            position: 'absolute', top: 0, left: 0, right: 0, p: 1,
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          }}>
            <Box display="flex" gap={0.5}>
              {isLeftover ? (
                <Chip
                  label="Leftover"
                  size="small"
                  sx={{ bgcolor: 'rgba(227,242,253,0.9)', color: '#1565c0', fontWeight: 600, fontSize: '0.7rem' }}
                />
              ) : (
                <Chip
                  label={mealTypeLabels[meal.meal_type] || meal.meal_type}
                  color={getMealTypeColor(meal.meal_type) as any}
                  size="small"
                  sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                />
              )}
            </Box>
          </Box>

          {/* Bottom overlay */}
          <Box sx={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
            p: 1.5, pt: 3,
          }}>
            {showDate && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem' }}>
                {dayjs(meal.assigned_date).format('ddd, MMM D')}
              </Typography>
            )}
            <Typography
              variant="body2"
              sx={{
                color: 'white', fontWeight: 700, lineHeight: 1.2,
                textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                overflow: 'hidden', textOverflow: 'ellipsis',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              }}
            >
              {meal.recipe.name}
            </Typography>
            {isLeftover && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.7rem' }}>
                Reheat {meal.servings} serving{meal.servings !== 1 ? 's' : ''}
              </Typography>
            )}
          </Box>
        </Box>
      </Card>
    </Badge>
  );
};

const TodaysDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPaid = user?.membership?.status === 'paid';
  const [loading, setLoading] = useState(true);
  const [upcomingMeals, setUpcomingMeals] = useState<Meal[]>([]);
  const [currentPlan, setCurrentPlan] = useState<MealPlan | null>(null);
  const [nextWeekPlan, setNextWeekPlan] = useState<MealPlan | null>(null);
  const [shoppingStatus, setShoppingStatus] = useState<ShoppingListStatus | null>(null);
  const [hasNextWeekPlan, setHasNextWeekPlan] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Use the same categorized-plans endpoint as Dashboard.tsx's full week view.
      // Previously this screen tried to bucket plans into Mon-Sun weeks via
      // /meal-plans/range, which broke when plan dates didn't align with the
      // user's current start-day preference (e.g. legacy plans created with
      // a different preference, or plans that span a non-standard 7 days).
      // The backend already returns activePlan / nextPlan / futurePlans
      // computed from actual meal dates — trust it.
      const plansRes = await api.get('/meal-plans/user-plans');
      const { activePlan, nextPlan, futurePlans } = plansRes.data;

      // "Current card" = plan in flight today, or promote the next future plan
      // if none is active. "Next card" = whatever comes after the current card.
      let currentSummary: any = null;
      let nextSummary: any = null;
      if (activePlan) {
        currentSummary = activePlan;
        nextSummary = nextPlan;
      } else if (nextPlan) {
        currentSummary = nextPlan;
        nextSummary = (futurePlans && futurePlans[0]) || null;
      }

      if (!currentSummary) {
        setCurrentPlan(null);
        setNextWeekPlan(null);
        setHasNextWeekPlan(false);
        return;
      }

      // Fetch full plan (with meals) for the current card
      const fullRes = await api.get(`/meal-plans/${currentSummary.plan_id}`);
      // Preserve the summary's accurate actual_start/end_date — /meal-plans/:id
      // returns the raw plan row which doesn't include them.
      const fullPlan = {
        ...fullRes.data,
        actual_start_date: currentSummary.actual_start_date,
        actual_end_date: currentSummary.actual_end_date,
      };
      setCurrentPlan(fullPlan);

      // Upcoming meals (today onward, next 4)
      const today = dayjs().startOf('day');
      const upcoming = (fullPlan.meals || [])
        .filter((meal: Meal) => dayjs(meal.assigned_date).startOf('day').isSameOrAfter(today, 'day'))
        .sort((a: Meal, b: Meal) => {
          const dateCompare = dayjs(a.assigned_date).diff(dayjs(b.assigned_date));
          if (dateCompare !== 0) return dateCompare;
          const order = { breakfast: 1, lunch: 2, dinner: 3 };
          return (order[a.meal_type as keyof typeof order] || 99) - (order[b.meal_type as keyof typeof order] || 99);
        })
        .slice(0, 4);
      setUpcomingMeals(upcoming);

      // Shopping list status (approved/active plans only)
      if (fullPlan.status === 'approved' || fullPlan.status === 'active') {
        try {
          const shoppingResponse = await api.get(`/shopping-list/${fullPlan.plan_id}`);
          const items = shoppingResponse.data.items || [];
          const total = items.length;
          const checked = items.filter((item: any) => item.checked).length;
          setShoppingStatus({ total_items: total, checked_items: checked, plan_id: fullPlan.plan_id });
        } catch (err) {
          console.error('Failed to fetch shopping list:', err);
        }
      }

      // Next-card display only needs dates + plan_id; no need to fetch meals.
      if (nextSummary) {
        setNextWeekPlan(nextSummary);
        setHasNextWeekPlan(true);
      } else {
        setNextWeekPlan(null);
        setHasNextWeekPlan(false);
      }
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getShoppingStatusBadge = () => {
    if (!shoppingStatus) return null;

    const { total_items, checked_items } = shoppingStatus;
    if (total_items === 0) return { text: 'No items', color: 'default' as const };
    if (checked_items === total_items) return { text: 'All set!', color: 'success' as const };
    if (checked_items > 0) return { text: `${total_items - checked_items} items left`, color: 'warning' as const };
    return { text: `${total_items} items`, color: 'error' as const };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  // No meal plan exists
  if (!currentPlan) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 6, px: 3 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Restaurant sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          {isPaid ? (
            <>
              <Typography variant="h4" gutterBottom>
                Ready to plan your week?
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Generate a personalized meal plan based on your family's preferences
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/dashboard?mode=generate')}
              >
                Generate Meal Plan
              </Button>
            </>
          ) : (
            <>
              <Typography variant="h4" gutterBottom>
                Discover meals your family will love
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Browse hundreds of recipes and add what looks great to your week.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/recipes')}
              >
                Browse Recipes
              </Button>
              <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  ✨ Want AI to plan your week for you?
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/membership')}
                >
                  Upgrade to Premium
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    );
  }

  // Check if plan is in the future (no meals for today/tomorrow).
  // Use actual_start_date when available — it's the real first-meal date,
  // accurate for partial-week plans. Parse as UTC so DATE columns serialized
  // as midnight-UTC don't shift back a day in US local timezones.
  const planStartDate = dayjs.utc(currentPlan.actual_start_date || currentPlan.week_start_date);
  const daysUntilPlanStarts = planStartDate.diff(dayjs(), 'day');

  if (daysUntilPlanStarts > 0 && upcomingMeals.length === 0) {
    const firstMeals = currentPlan.meals
      ?.sort((a: Meal, b: Meal) => {
        const dateCompare = dayjs(a.assigned_date).diff(dayjs(b.assigned_date));
        if (dateCompare !== 0) return dateCompare;
        const order = { breakfast: 1, lunch: 2, dinner: 3 };
        return (order[a.meal_type as keyof typeof order] || 99) - (order[b.meal_type as keyof typeof order] || 99);
      })
      .slice(0, 4) || [];

    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, px: 3 }}>
        {/* Header */}
        <Typography variant="h4" gutterBottom sx={{ textAlign: 'center' }}>
          {dayjs().format('dddd, MMMM D')}
        </Typography>

        {/* Upcoming Plan Banner */}
        <Card sx={{ mb: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CalendarMonth color="primary" />
              <Typography variant="h6" color="primary.main">
                Meal Plan Ready!
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Your meal plan starts on {planStartDate.format('dddd, MMMM D')} ({daysUntilPlanStarts} {daysUntilPlanStarts === 1 ? 'day' : 'days'} from now).
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<CalendarMonth />}
                onClick={() => navigate(`/dashboard?mode=plan&planId=${currentPlan.plan_id}`)}
              >
                View Full Plan
              </Button>
              {(currentPlan.status === 'approved' || currentPlan.status === 'active') && (
                <Button
                  variant="outlined"
                  startIcon={<ShoppingCart />}
                  onClick={() => navigate(`/shopping-list/${currentPlan.plan_id}`)}
                >
                  Shopping List
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Preview of first meals — image cards */}
        {firstMeals.length > 0 && (
          <>
            <Typography variant="h5" sx={{ mb: 2 }}>
              First Meals
            </Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 2,
            }}>
              {firstMeals.map((meal: Meal) => (
                <DashboardMealCard
                  key={meal.meal_id}
                  meal={meal}
                  showDate
                  onClick={() => navigate(`/meal/${meal.recipe.recipe_id}?meal_id=${meal.meal_id}`)}
                />
              ))}
            </Box>
          </>
        )}

        {/* Plan Next Week if no next week plan — paid users only */}
        {!hasNextWeekPlan && isPaid && (
          <Card sx={{ mt: 4 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AddCircleOutline color="primary" />
                <Typography variant="h6">Plan the Week After</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Get ahead by planning your meals for the following week
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate('/dashboard?mode=generate')}
              >
                Generate Plan
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Browse more recipes — always visible for free users */}
        {!isPaid && (
          <Card sx={{ mt: 4 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AddCircleOutline color="primary" />
                <Typography variant="h6">Add More Meals</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Browse our recipe library and add more meals to your week.
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate('/recipes')}
              >
                Browse Recipes
              </Button>
            </CardContent>
          </Card>
        )}
      </Box>
    );
  }

  // Plan needs approval
  if (currentPlan.status === 'planned') {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 6, px: 3 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Meal Plan Needs Approval
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Review your meal plan and approve it to start using your recipes and shopping list.
          </Typography>
          <Button
            variant="contained"
            color="warning"
            onClick={() => navigate(`/meal-plans/${currentPlan.plan_id}/review`)}
          >
            Review & Approve Now
          </Button>
        </Alert>
      </Box>
    );
  }

  const statusBadge = getShoppingStatusBadge();

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, px: 3 }}>
      {/* Header */}
      <Typography variant="h4" gutterBottom sx={{ textAlign: 'center' }}>
        {dayjs().format('dddd, MMMM D')}
      </Typography>

      {/* Upcoming Meals */}
      <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
        Upcoming Meals
      </Typography>

      {upcomingMeals.length === 0 ? (
        <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
            No upcoming meals scheduled
          </Typography>
        </Paper>
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: 2,
        }}>
          {upcomingMeals.map((meal) => (
            <DashboardMealCard
              key={meal.meal_id}
              meal={meal}
              height={160}
              showDate
              onClick={() => navigate(`/meal/${meal.recipe.recipe_id}?meal_id=${meal.meal_id}`)}
            />
          ))}
        </Box>
      )}

      {/* Quick Actions */}
      <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
        Quick Actions
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        {/* This Week's Plan */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CalendarMonth color="primary" />
              <Typography variant="h6">This Week's Plan</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {dayjs.utc(currentPlan.actual_start_date || currentPlan.week_start_date).format('MMM D')}
              {' - '}
              {currentPlan.actual_end_date
                ? dayjs.utc(currentPlan.actual_end_date).format('MMM D')
                : dayjs.utc(currentPlan.week_start_date).add(6, 'day').format('MMM D')}
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => navigate('/dashboard?mode=week')}
            >
              View Full Week
            </Button>
          </CardContent>
        </Card>

        {/* Next Week's Plan */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CalendarMonth color="primary" />
              <Typography variant="h6">Next Week's Plan</Typography>
            </Box>
            {hasNextWeekPlan && nextWeekPlan ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {dayjs.utc(nextWeekPlan.actual_start_date || nextWeekPlan.week_start_date).format('MMM D')}
                  {' - '}
                  {nextWeekPlan.actual_end_date
                    ? dayjs.utc(nextWeekPlan.actual_end_date).format('MMM D')
                    : dayjs.utc(nextWeekPlan.week_start_date).add(6, 'day').format('MMM D')}
                </Typography>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate(`/dashboard?mode=plan&planId=${nextWeekPlan.plan_id}`)}
                >
                  View Plan
                </Button>
              </>
            ) : isPaid ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Get ahead by planning your meals for next week
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => navigate('/dashboard?mode=generate')}
                >
                  Generate Plan
                </Button>
              </>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Upgrade to Premium and plan your week automatically with AI.
                </Typography>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate('/membership')}
                >
                  Upgrade to Premium
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Shopping List — full width */}
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart color="primary" />
              <Typography variant="h6">Shopping List</Typography>
            </Box>
            {statusBadge && (
              <Chip
                label={statusBadge.text}
                color={statusBadge.color}
                size="small"
              />
            )}
          </Box>
          {shoppingStatus && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {shoppingStatus.checked_items} of {shoppingStatus.total_items} items checked off
            </Typography>
          )}
          <Button
            variant="outlined"
            fullWidth
            onClick={() => navigate(`/shopping-list/${currentPlan.plan_id}`)}
          >
            View List
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TodaysDashboard;
